import React, {
    createContext, useState, useContext, useCallback, useMemo
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import Toast from '../../components/UIElements/Toast';

const ToastContext = createContext();

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback(({
        type = 'info', title, message, position = 'bottom-right', duration = 3000
    }) => {
        const id = uuidv4();
        const newToast = {
            id,
            type,
            title: title || `${type.charAt(0).toUpperCase() + type.slice(1)}`,
            message,
            position,
            duration,
            isOpen: true,
        };

        setToasts((prevToasts) => [...prevToasts, newToast]);
        return id;
    }, []);

    const closeToast = useCallback((id) => {
        setToasts((prevToasts) => prevToasts.map((toast) => (toast.id === id ? { ...toast, isOpen: false } : toast)));
    }, []);

    const removeToast = useCallback((id) => {
        setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
    }, []);

    // Helper functions for convenience
    const successToast = useCallback(
        (message, options = {}) => showToast({ type: 'success', message, ...options }),
        [showToast]
    );
    const errorToast = useCallback(
        (message, options = {}) => showToast({ type: 'error', message, ...options }),
        [showToast]
    );
    const warningToast = useCallback(
        (message, options = {}) => showToast({ type: 'warning', message, ...options }),
        [showToast]
    );
    const infoToast = useCallback(
        (message, options = {}) => showToast({ type: 'info', message, ...options }),
        [showToast]
    );

    const contextValue = useMemo(() => ({
        showToast, successToast, errorToast, warningToast, infoToast, closeToast, removeToast
    }), [showToast, successToast, errorToast, warningToast, infoToast, closeToast, removeToast]);

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            {toasts.map((toast) => (
                <Toast
                    key={toast.id}
                    isOpen={toast.isOpen}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                    position={toast.position}
                    duration={toast.duration}
                    onClose={() => closeToast(toast.id)}
                    onExited={() => removeToast(toast.id)}
                />
            ))}
        </ToastContext.Provider>
    );
};

export default ToastProvider;
